// 알림 발송 유틸리티
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// 알림 타입 상수
export const NOTIFICATION_TYPES = {
  LOCATION_CHANGE: 'location_change',
  QUANTITY_CHANGE: 'quantity_change',
  SALE: 'sale',
  LOCATION_ZONE_MISMATCH: 'location_zone_mismatch'
};

/**
 * 알림을 발송하는 메인 함수
 * @param {string} eventType - 알림 타입 (location_change, quantity_change, sale)
 * @param {Object} data - 알림 데이터
 * @param {string} data.productId - 제품 ID
 * @param {string} data.productName - 제품명
 * @param {string} data.message - 알림 메시지
 * @param {string} data.previousValue - 이전 값 (선택사항)
 * @param {string} data.newValue - 새로운 값 (선택사항)
 * @param {string} triggeredBy - 알림을 발생시킨 사용자 이메일
 */
export const sendNotification = async (eventType, data, triggeredBy) => {
  try {
    // 1. 알림 수신 대상 사용자 가져오기
    const recipients = await getNotificationRecipients(eventType);

    if (recipients.length === 0) {
      console.log('알림 수신 대상이 없습니다.');
      return;
    }

    // 2. 각 수신자에게 알림 생성
    const notifications = recipients.map(async (recipient) => {
      // 앱 내 알림만 활성화된 경우에만 Firestore에 알림 생성
      if (recipient.inAppEnabled) {
        await createInAppNotification(recipient.userId, eventType, data, triggeredBy);
      }

      // 브라우저 푸시 알림은 Firebase Cloud Functions로 처리 (별도 구현 필요)
      // FCM을 통한 푸시 알림은 서버 사이드에서 처리해야 합니다.
    });

    await Promise.all(notifications);

    console.log(`알림 발송 완료: ${eventType}`, data);
  } catch (error) {
    console.error('알림 발송 오류:', error);
  }
};

/**
 * 알림 수신 대상 사용자 목록 가져오기
 * @param {string} eventType - 알림 타입
 * @returns {Array} 수신자 목록
 */
const getNotificationRecipients = async (eventType) => {
  try {
    const preferencesRef = collection(db, 'user_notification_preferences');
    const snapshot = await getDocs(preferencesRef);

    const recipients = [];

    // 설정이 없으면 기본 사용자 목록 반환 (모든 사용자에게 알림)
    if (snapshot.empty) {
      // 기본값으로 모든 알림 활성화된 사용자 반환
      return [
        { userId: 'leehoins@gmail.com', inAppEnabled: true, browserPushEnabled: false },
        { userId: 'oneman0318@naver.com', inAppEnabled: true, browserPushEnabled: false }
      ];
    }

    snapshot.forEach((doc) => {
      const prefs = doc.data();

      // 알림 타입에 따라 수신 여부 확인
      let shouldReceive = false;

      switch (eventType) {
        case NOTIFICATION_TYPES.LOCATION_CHANGE:
          shouldReceive = prefs.notificationTypes?.locationChange !== false; // 기본값 true
          break;
        case NOTIFICATION_TYPES.QUANTITY_CHANGE:
          shouldReceive = prefs.notificationTypes?.quantityChange !== false;
          break;
        case NOTIFICATION_TYPES.SALE:
          shouldReceive = prefs.notificationTypes?.sale !== false;
          break;
        case NOTIFICATION_TYPES.LOCATION_ZONE_MISMATCH:
          shouldReceive = prefs.notificationTypes?.locationZoneMismatch !== false; // 기본값 true
          break;
        default:
          shouldReceive = true;
      }

      // 앱 내 알림이 명시적으로 비활성화되지 않은 경우 알림 발송
      const inAppEnabled = prefs.inAppEnabled !== false;
      if (inAppEnabled && shouldReceive) {
        recipients.push({
          userId: prefs.userId,
          inAppEnabled: true,
          browserPushEnabled: prefs.browserPushEnabled || false
        });
      }
    });

    return recipients;
  } catch (error) {
    console.error('수신자 목록 조회 오류:', error);
    // 오류 시에도 기본 사용자에게 알림
    return [
      { userId: 'leehoins@gmail.com', inAppEnabled: true, browserPushEnabled: false },
      { userId: 'oneman0318@naver.com', inAppEnabled: true, browserPushEnabled: false }
    ];
  }
};

/**
 * 앱 내 알림 생성 (Firestore에 저장)
 * @param {string} userId - 사용자 ID (이메일)
 * @param {string} eventType - 알림 타입
 * @param {Object} data - 알림 데이터
 * @param {string} triggeredBy - 변경을 수행한 사용자 이메일
 */
const createInAppNotification = async (userId, eventType, data, triggeredBy) => {
  try {
    // 알림 제목 생성
    const title = getNotificationTitle(eventType, data);

    // 알림 메시지 생성
    const message = data.message || getNotificationMessage(eventType, data);

    // Firestore에 알림 저장
    await addDoc(collection(db, 'user_notifications'), {
      userId,
      type: eventType,
      title,
      message,
      productId: data.productId,
      productName: data.productName,
      triggeredBy: triggeredBy || null, // 수정자 정보 추가
      read: false,
      createdAt: serverTimestamp()
    });

    console.log(`앱 내 알림 생성: ${userId} - ${title} (수정자: ${triggeredBy || '알 수 없음'})`);
  } catch (error) {
    console.error('앱 내 알림 생성 오류:', error);
  }
};

/**
 * 알림 타입에 따른 제목 생성
 * @param {string} eventType - 알림 타입
 * @param {Object} data - 알림 데이터
 * @returns {string} 알림 제목
 */
const getNotificationTitle = (eventType, data) => {
  switch (eventType) {
    case NOTIFICATION_TYPES.LOCATION_CHANGE:
      return '재고 위치 이동';
    case NOTIFICATION_TYPES.QUANTITY_CHANGE:
      return '수량 변경';
    case NOTIFICATION_TYPES.SALE:
      return '판매 완료';
    case NOTIFICATION_TYPES.LOCATION_ZONE_MISMATCH:
      return '⚠️ 위치/Zone 불일치';
    default:
      return '알림';
  }
};

/**
 * 알림 타입에 따른 메시지 생성
 * @param {string} eventType - 알림 타입
 * @param {Object} data - 알림 데이터
 * @returns {string} 알림 메시지
 */
const getNotificationMessage = (eventType, data) => {
  const productName = data.productName || '제품';

  switch (eventType) {
    case NOTIFICATION_TYPES.LOCATION_CHANGE:
      if (data.previousValue && data.newValue) {
        return `${productName} - ${data.previousValue} → ${data.newValue}`;
      }
      return `${productName}의 위치가 변경되었습니다`;

    case NOTIFICATION_TYPES.QUANTITY_CHANGE:
      if (data.previousValue && data.newValue) {
        return `${productName} - 수량: ${data.previousValue} → ${data.newValue}`;
      }
      return `${productName}의 수량이 변경되었습니다`;

    case NOTIFICATION_TYPES.SALE:
      return `${productName}이(가) 판매 완료되었습니다`;

    case NOTIFICATION_TYPES.LOCATION_ZONE_MISMATCH:
      if (data.actualLocation && data.zoneLocation) {
        return `${productName} - 위치(${data.actualLocation})와 Zone(${data.zoneLocation})이 일치하지 않습니다. 확인이 필요합니다.`;
      }
      return `${productName}의 위치와 Zone이 일치하지 않습니다. 확인이 필요합니다.`;

    default:
      return `${productName}에 변경사항이 있습니다`;
  }
};

/**
 * 간편 알림 함수들
 */

// 위치 변경 알림
export const sendLocationChangeNotification = async (productId, productName, previousLocation, newLocation, triggeredBy) => {
  await sendNotification(
    NOTIFICATION_TYPES.LOCATION_CHANGE,
    {
      productId,
      productName,
      previousValue: previousLocation,
      newValue: newLocation
    },
    triggeredBy
  );
};

// 수량 변경 알림
export const sendQuantityChangeNotification = async (productId, productName, previousQuantity, newQuantity, triggeredBy) => {
  await sendNotification(
    NOTIFICATION_TYPES.QUANTITY_CHANGE,
    {
      productId,
      productName,
      previousValue: previousQuantity.toString(),
      newValue: newQuantity.toString()
    },
    triggeredBy
  );
};

// 판매 완료 알림
export const sendSaleNotification = async (productId, productName, triggeredBy) => {
  await sendNotification(
    NOTIFICATION_TYPES.SALE,
    {
      productId,
      productName
    },
    triggeredBy
  );
};

// 위치/Zone 불일치 알림
export const sendLocationZoneMismatchNotification = async (productId, productName, actualLocation, zoneLocation, triggeredBy) => {
  await sendNotification(
    NOTIFICATION_TYPES.LOCATION_ZONE_MISMATCH,
    {
      productId,
      productName,
      actualLocation,
      zoneLocation
    },
    triggeredBy
  );
};
